<template>
    <div id="question-setting-section">
        <div class="sticky sticky-box">
            <div class="question-option flex p-4">
                <div
                    :class="[
                        {
                            'create-question-box flex': true,
                            'flex-auto': questionSelect.length,
                            'md:flex-1': !questionSelect.length
                        }
                    ]"
                >
                    <XButton
                        @click="openQuestionSetting('create')"
                        :disabled="!canEditQuiz"
                        class="shadow-text-none w-full"
                        bg-color="#28a745"
                        font-weight="400"
                        id="button-add-question"
                    >
                        <span>{{ $t('question_button_add_question') }}</span>
                    </XButton>
                    <XButton
                        v-if="quiz.type != 'poll' && allowRobotQuiz && !isStudent"
                        v-tippy="{ placement: 'top' }"
                        @click="showRobot = true"
                        :content="$t('question_modal_generate_question')"
                        :disabled="!canEditQuiz"
                        class="robot-btn shadow-text-none"
                        bg-color="#28a745"
                        font-weight="400"
                        id="button-robot-question"
                    >
                        <i class="fas fa-robot"></i>
                    </XButton>
                </div>

                <div class="flex-1 flex col-gap-8">
                    <span
                        v-if="isCreator"
                        v-tippy="{ placement: 'top' }"
                        :content="
                            checkStatusApproval
                                ? $t('question_tooltip_save_before_approval')
                                : $t('question_button_submit_approval')
                        "
                        class="flex-1"
                    >
                        <XButton
                            @click="submitApproval()"
                            :disabled="!canEditQuiz || processing || checkStatusApproval"
                            class="shadow-text-none w-full"
                            bg-color="#7a36d4"
                            font-weight="400"
                        >
                            <i class="far fa-paper-plane"></i>
                            <span class="hidden sm:block">{{ $t('question_button_submit_approval') }}</span>
                        </XButton>
                    </span>
                    <XButton
                        @click="saveQuiz()"
                        :disabled="!canEditQuiz || processing"
                        class="flex-1 shadow-text-none"
                        bg-color="#17a2b8"
                        font-weight="400"
                    >
                        <i class="far fa-save"></i>
                        <span :class="[{ 'sm:block': true, hidden: isCreator }]">
                            {{ $t('question_button_save_quiz') }}
                        </span>
                    </XButton>
                </div>

                <div v-if="questionSelect.length" class="flex justify-between question-sub-option">
                    <XButton
                        v-tippy="{ placement: 'top' }"
                        @click="showQuestionEditText = true"
                        :disabled="!canEditQuiz"
                        :content="$t('question_modal_edit_text_question', { s: questionSelect.length > 1 ? 's' : '' })"
                        class="shadow-text-none"
                        bg-color="#343a40"
                    >
                        <i class="far fa-edit"></i>
                    </XButton>
                    <XButton
                        v-tippy="{ placement: 'top' }"
                        @click="duplicateQuestion()"
                        :content="$t('question_modal_duplicate_question', { s: questionSelect.length > 1 ? 's' : '' })"
                        :disabled="!canEditQuiz || processing"
                        class="shadow-text-none"
                        bg-color="#ffc107"
                    >
                        <i class="far fa-clone"></i>
                    </XButton>
                    <XButton
                        v-tippy="{ placement: 'top' }"
                        @click="removeQuestion()"
                        :content="$t('question_modal_remove_question', { s: questionSelect.length > 1 ? 's' : '' })"
                        :disabled="!canEditQuiz || processing"
                        class="shadow-text-none"
                        bg-color="#dc3545"
                    >
                        <i class="far fa-trash-alt trash-icon"></i>
                    </XButton>
                </div>
            </div>
        </div>
        <div class="my-4 px-4">
            <div class="search-box">
                <XInput :value="searchValue" @input="e => (searchValue = e.target.value)" placeholder="Search" />
            </div>
        </div>
        <div class="flex justify-between items-center shadow-text-none px-4">
            <div>
                <XCheckbox
                    @input="selectAllQuestions($event)"
                    :square="true"
                    :disabled="!canEditQuiz || !cacheQuestions.length"
                    id="select-all"
                    type="checkbox"
                    size="lg"
                    theme="dark"
                >
                    <label for="select-all" class="font-bold shadow-text-none">
                        {{ $t('question_checkbox_select_all') }}
                    </label>
                </XCheckbox>
            </div>
            <span>{{ questionSelect.length }}/{{ cacheQuestions.length }}</span>
        </div>
        <draggable
            v-if="cacheQuestions.length"
            v-model="cacheQuestions"
            @update="endSorting()"
            :options="{ disabled: disableDraggable || !canEditQuiz }"
            ref="draggable"
            handle=".draggable-box"
            ghost-class="ghost"
        >
            <div
                v-for="(item, index) in cacheQuestions"
                :key="index"
                :class="{
                    'relative question-list flex justify-between items-center mt-4': true
                }"
            >
                <div class="flex w-full h-full">
                    <div class="flex-1 flex justify-content items-center pt-4 pb-4 pl-4 relative">
                        <CrownIcon v-if="item.question_type != 'basic'" class="font-size-12" />
                        <XCheckbox
                            @input="selectQuestion($event, item)"
                            :id="`checkbox-${index + 1}`"
                            :square="true"
                            :show-content="false"
                            :checked="
                                questionSelect.find(e => JSON.stringify(e) === JSON.stringify(item)) ? true : false
                            "
                            :disabled="!canEditQuiz"
                            type="checkbox"
                            size="lg"
                            theme="dark"
                            class="intro-checkbox"
                        />
                    </div>
                    <div
                        @click="openQuestionSetting('edit', index)"
                        class="flex col-gap-16 w-full overflow-hidden py-4 pl-4 cursor-pointer"
                    >
                        <div v-if="item.image" class="question-image-box h-full p-1">
                            <div
                                :style="`background-image: url(${item.image})`"
                                class="question-image w-full h-full"
                            ></div>
                        </div>
                        <div class="flex flex-col justify-between w-full overflow-hidden">
                            <p class="question-title font-bold shadow-text-none">
                                {{ item.title ? item.title : 'Untitled' }}
                            </p>
                            <div class="flex flex-col row-gap-4">
                                <span class="font-size-12 text-grey shadow-text-none capitalize">
                                    <template v-if="quiz.type == 'quiz'">
                                        {{ checkQuestionTypeName(item.question_type) }}
                                    </template>
                                    <template v-if="quiz.type == 'poll'">
                                        <i
                                            v-if="item.chart_type == 'bar'"
                                            class="fas fa-chart-bar shadow-text-none"
                                            style="transform: rotate(90deg)"
                                        ></i>
                                        <i
                                            v-if="item.chart_type == 'pie'"
                                            class="fas fa-chart-pie shadow-text-none"
                                        ></i>
                                        {{ item.chart_type }}
                                    </template>
                                </span>
                                <div class="flex items-center col-gap-8 font-size-12 text-grey">
                                    <div class="flex items-center col-gap-8">
                                        <div class="shadow-text-none font-size-16">
                                            <i v-if="item.status" class="fas fa-check-circle"></i>
                                            <i v-else class="far fa-circle"></i>
                                        </div>
                                        <span class="shadow-text-none">{{ $t('question_active') }}</span>
                                    </div>
                                    <div v-if="quiz.type != 'poll'" class="flex items-center col-gap-8">
                                        <div class="shadow-text-none font-size-16">
                                            <i v-if="item.is_mandatory" class="fas fa-check-circle"></i>
                                            <i v-else class="far fa-circle"></i>
                                        </div>
                                        <span class="shadow-text-none">{{ $t('question_mandatory') }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div
                    v-if="!disableDraggable"
                    :class="[
                        {
                            'draggable-box flex justify-center items-center text-grey shadow-text-none p-4': true,
                            disabled: !canEditQuiz
                        }
                    ]"
                >
                    <i class="fas fa-ellipsis-v"></i>
                    <i class="fas fa-ellipsis-v"></i>
                </div>
            </div>
        </draggable>
        <div v-else class="question-list flex justify-center items-center mt-4">
            <span class="shadow-text-none">No Question</span>
        </div>

        <QuestionSettingModal
            v-if="showQuestionSetting"
            @close="saveQuestions($event)"
            @open-share-validate-modal="validateToShare()"
            :questions="questions"
            ref="questionSettingModal"
        />
        <QuestionEditTextModal
            v-if="showQuestionEditText"
            @close="showQuestionEditText = false"
            @update="saveQuestions()"
            :question-select="questionSelect"
        />
        <RobotModal
            v-if="quiz.type != 'poll' && allowRobotQuiz && showRobot"
            @close="showRobot = false"
            @add="addRobotQuestions($event)"
            ref="robotModal"
        />
    </div>
</template>

<script>
import { mapState } from 'vuex'
import helper from '@/mixins/helper'
import { currentUser, validateToken } from '@/lib/user.js'
import { load } from 'recaptcha-v3'
import QuestionSettingModal from '@/components/quiz-creator/question-section/QuestionSettingModal'
import QuestionEditTextModal from '@/components/quiz-creator/question-section/QuestionEditTextModal'
import XInput from '@/components/v2/XInput'
import XButton from '@/components/v2/XButton'
import XCheckbox from '@/components/v2/XCheckbox'
import draggable from 'vuedraggable'
import popup from '@/lib/popup'
import IntroHint from '@/lib/intro.js'
import introJsData from '@/mixins/introjs-data'
import quizCreator from '@/mixins/quiz-creator.js'
import RobotModal from '@/components/quiz-creator/question-section/RobotModal'
import CrownIcon from '@/components/CrownIcon'

export default {
    components: {
        QuestionSettingModal,
        QuestionEditTextModal,
        XInput,
        XButton,
        XCheckbox,
        draggable,
        RobotModal,
        CrownIcon
    },

    mixins: [helper, quizCreator, introJsData],

    data() {
        return {
            showQuestionSetting: false,
            showQuestionEditText: false,
            cacheQuestions: null,
            questionSelect: [],
            disableDraggable: false,
            searchValue: '',
            introHint: new IntroHint(),
            showRobot: false,
            processing: false
        }
    },

    created() {
        let storageName

        if (!this.id) {
            if (this.quiz.type == 'quiz') {
                storageName = 'cache-quiz'
            }

            if (this.quiz.type == 'poll') {
                storageName = 'cache-poll'
            }
        } else {
            storageName = this.id
        }

        if (!localStorage.getItem(storageName)) {
            this.$store.commit('getOnlyUidQuestions')
        }

        this.cacheQuestions = JSON.parse(JSON.stringify(this.questions))

        this.introHint.removeHints()
        setTimeout(() => {
            if (!this.allowRobotQuiz && !this.showRobot) {
                const indexToDelete = this.hintsQuestionList.groupQuestionList.findIndex(
                    question => question.element === '#button-robot-question'
                )

                if (indexToDelete !== -1) {
                    this.hintsQuestionList.groupQuestionList.splice(indexToDelete, 1)
                }
            }

            this.introHint.register(this.hintsQuestionList)
        }, 500)
    },

    mounted() {
        this.stickyBox()
    },

    watch: {
        searchValue() {
            this.questionSelect = []
            this.fetchQuestion()
        },

        questionSelect(data) {
            this.introHint.removeHints()
            if (this.cacheQuestions.length && data.length === this.cacheQuestions.length) {
                document.querySelector('#select-all').checked = true
            } else {
                document.querySelector('#select-all').checked = false
            }
        },

        processing(value) {
            this.$emit('processing', value)
        }
    },

    computed: {
        ...mapState({
            id: state => state.question._id,
            quiz: state => state.question,
            questions: state => state.question.questions,
            isCreateNewQuiz: state => state.isCreateNewQuiz,
            showSchoolList: state => state.question.show_school_list,
            showSchoolCountryList: state => state.question.show_school_country_list,
            classSubject: state => state.question.class_subject,
            classGrade: state => state.question.class_grade,
            description: state => state.question.description,
            age: state => state.question.age,
            difficult: state => state.question.difficult,
            image: state => state.question.image,
            minimumQuestionToShare: state => state.minimumQuestionToShare,
            showNumberQuestion: state => state.question.show_number_question
        }),

        isCreator() {
            return ['contributor', 'creator'].indexOf(currentUser().role) > -1 && this.quiz.user_id === currentUser().id
        },

        isStudent() {
            return ['student', 'student_plus'].includes(currentUser().role)
        },

        allowRobotQuiz() {
            return !currentUser().disable_quiz_ai ? true : false
        },

        checkStatusApproval() {
            return this.isApprovalPendingStatus || !this.quiz._id || this.quiz._id.$oid === 'mock'
        }
    },

    methods: {
        fetchQuestion() {
            this.cacheQuestions = JSON.parse(JSON.stringify(this.questions))
            this.disableDraggable = false

            if (this.searchValue) {
                this.cacheQuestions = this.cacheQuestions.filter(e =>
                    e.title.toLowerCase().includes(this.searchValue.toLowerCase())
                )

                this.disableDraggable = true
            }
        },

        async endSorting() {
            if (!this.canEditQuiz) return

            this.$store.commit('updateQuestionList', this.cacheQuestions)

            if (this.id) {
                const res = await this.$store.dispatch('updateQuizQuestion')
                this.$emit('update-questions', res.questions)
            } else {
                this.setCacheData(this.quiz)
            }
        },

        async openQuestionSetting(headerType, questionIndex) {
            if (this.showQuestionSetting) return

            if (headerType == 'create') {
                this.$store.commit('addNewQuestion')
                questionIndex = this.questions.length - 1
                this.questions[questionIndex].onCreate = true
            }

            if (headerType == 'edit') {
                questionIndex = this.questions.findIndex(
                    e => JSON.stringify(e) === JSON.stringify(this.cacheQuestions[questionIndex])
                )

                for (const item of this.questions) {
                    delete item.onCreate
                }
            }

            this.showQuestionSetting = true

            await this.$nextTick()

            this.$refs.questionSettingModal.showModal(headerType, questionIndex)
        },

        async saveQuestions(cache) {
            const close = () => {
                if (this.showQuestionSetting) {
                    this.$refs.questionSettingModal.closeModal()
                }

                this.introHint.removeHints()

                setTimeout(() => {
                    this.introHint.register(this.hintsQuestionList)
                    this.showQuestionSetting = false
                    this.showQuestionEditText = false
                    this.processing = false
                }, 300)
            }

            if (!this.canEditQuiz || this.processing || JSON.stringify(this.questions) === JSON.stringify(cache)) {
                close()

                return
            }

            this.processing = true

            for (const item of this.questions) {
                delete item.onCreate
            }

            this.questionSelect = []

            if (this.id) {
                const res = await this.$store.dispatch('updateQuizQuestion', {
                    logMessage: 'create/update',
                    logQuestions: this.questions
                })

                this.$emit('update-questions', res.questions)
            } else {
                this.setCacheData(this.quiz)
            }

            if (this.canEditQuiz) {
                this.cacheQuestions = JSON.parse(JSON.stringify(this.questions))

                if (this.searchValue) {
                    this.cacheQuestions = this.cacheQuestions.filter(e =>
                        e.title.toLowerCase().includes(this.searchValue.toLowerCase())
                    )
                }
            }

            close()
        },

        async duplicateQuestion() {
            if (!this.canEditQuiz || this.processing) return

            this.processing = true

            for (const item of this.questionSelect) {
                const question = Object.assign(
                    {},
                    this.questions.find(e => {
                        const tempE = { ...e }
                        const tempItem = { ...item }

                        delete tempE.uid
                        delete tempItem.uid

                        return JSON.stringify(tempE) === JSON.stringify(tempItem)
                    })
                )

                const title = `${question.title} (copy)`
                question.title = title

                let duplicate = this.questions.find(e => e.title === question.title) ? true : false
                let duplicateCount = this.questions.filter(e => e.title === question.title).length

                while (duplicate) {
                    question.title = `${title} ${duplicateCount}`
                    duplicateCount++

                    if (!this.questions.find(e => e.title === question.title)) {
                        duplicate = false
                    }
                }

                delete question.uid

                this.$store.commit('duplicateQuestion', JSON.parse(JSON.stringify(question)))
            }

            if (this.id) {
                const logQuestions = [
                    ...this.questionSelect.map(select => {
                        return { title: select.title }
                    })
                ]

                const res = await this.$store.dispatch('updateQuizQuestion', {
                    logMessage: 'clone',
                    logQuestions
                })
                this.$emit('update-questions', res.questions)
            } else {
                this.setCacheData(this.quiz)
            }

            const duplicateLength = this.questionSelect.length

            this.questionSelect = []
            this.searchValue = ''
            this.fetchQuestion()

            this.processing = false

            await this.$nextTick()

            const handleScroll = () => {
                if (modalBody.scrollTop + modalBody.clientHeight >= modalBody.scrollHeight) {
                    for (let i = 1; i <= duplicateLength; i++) {
                        const questionList = document.querySelectorAll('.question-list')

                        questionList[questionList.length - i].classList.add('new')

                        setTimeout(() => {
                            questionList[questionList.length - i].classList.remove('new')
                        }, 3000)
                    }

                    modalBody.removeEventListener('scroll', handleScroll)
                }
            }

            const modalBody = document.querySelector('#quiz-creator-modal .modal-body > div')

            modalBody.addEventListener('scroll', handleScroll)

            modalBody.scrollTo(0, modalBody.scrollHeight)
        },

        removeQuestion() {
            if (!this.canEditQuiz || this.processing) return

            popup.confirm(
                this.$t('question_alert_delete_select_question', {
                    select: this.questionSelect.length,
                    total: this.cacheQuestions.length,
                    s: this.questionSelect.length > 1 ? 's' : ''
                }),
                async () => {
                    this.processing = true

                    for (const item of this.questionSelect) {
                        this.$store.commit(
                            'removeQuestion',
                            this.questions.findIndex(e => JSON.stringify(e) === JSON.stringify(item))
                        )
                    }

                    if (this.id) {
                        const logQuestions = [
                            ...this.questionSelect.map(select => {
                                return { title: select.title }
                            })
                        ]

                        const res = await this.$store.dispatch('updateQuizQuestion', {
                            logMessage: 'delete',
                            logQuestions
                        })
                        this.$emit('update-questions', res.questions)
                    } else {
                        this.setCacheData(this.quiz)
                    }

                    this.questionSelect = []
                    this.fetchQuestion()

                    this.processing = false

                    if (
                        this.questions.filter(question => question.status).length < this.minimumQuestionToShare &&
                        (this.showSchoolList || this.showSchoolCountryList)
                    ) {
                        this.validateToShare()
                        return
                    }
                }
            )
        },

        selectAllQuestions(event) {
            if (!this.canEditQuiz) return

            let checked = false

            if (event) {
                checked = event.target.checked
            }

            if (checked) {
                this.questionSelect = JSON.parse(JSON.stringify(this.cacheQuestions))
            } else {
                this.questionSelect = []
            }
        },

        selectQuestion(event, data) {
            if (!this.canEditQuiz) return

            const checked = event.target.checked

            if (checked) {
                this.questionSelect.push(data)

                if (this.questionSelect.length === this.cacheQuestions.length) {
                    document.querySelector('#select-all').checked = true
                }
            } else {
                this.questionSelect.splice(
                    this.questionSelect.findIndex(e => JSON.stringify(e) === JSON.stringify(data)),
                    1
                )

                document.querySelector('#select-all').checked = false
            }
        },

        reCaptchaValidate() {
            return new Promise((resolve, reject) => {
                load(this.getRecaptchaSiteKey(), {
                    useRecaptchaNet: true,
                    autoHideBadge: true
                })
                    .then(async recaptcha => {
                        await this.$store.commit(
                            'setReCaptchaResponse',
                            await recaptcha.execute('validate_captcha', { action: 'validate_captcha' })
                        )
                        resolve()
                    })
                    .catch(() => {
                        reject()
                    })
            })
        },

        getRecaptchaSiteKey() {
            return process.env.VUE_APP_RECAPTCHA_SITE_KEY
        },

        submitApproval() {
            if (!this.canEditQuiz) return

            this.$emit('open-approval-modal')
        },

        async saveQuiz() {
            if (!this.canEditQuiz || this.processing) return

            this.processing = true

            this.$store.commit('toggleMainLoader', true)

            const validate = await this.validateQuiz()

            if (!validate) {
                this.processing = false
                this.$store.commit('toggleMainLoader', false)
                return false
            }

            let save
            const payload = {}
            const userService = await validateToken()

            if (userService) {
                await this.reCaptchaValidate()
            }

            if (!this.id) {
                payload.newQuiz = true
                save = await this.$store.dispatch('createQuestion')
            } else {
                payload.newQuiz = false
                save = await this.$store.dispatch('updateQuiz', { update: true })
            }

            this.processing = false

            if (!save) {
                this.toastError(this.$t('question_alert_not_successfully'))
                return false
            }

            this.toastSuccess(
                payload.newQuiz
                    ? this.$t('question_alert_create_quiz_successfully')
                    : this.$t('question_alert_update_quiz_successfully')
            )

            this.fetchQuestion()

            this.$emit('completed', payload)

            return true
        },

        validateQuiz() {
            if (!this.quiz.title) {
                popup.alert('The Quiz should have title.', () => {
                    this.$emit('open-config', 'quiz')
                })

                return false
            }

            if (this.quiz.background_type == 'image' && !this.quiz.background_image) {
                popup.alert('The background image should have image.', () => {
                    this.$emit('open-config', 'theme')
                })

                return false
            }

            if (!this.questions.length) {
                popup.alert('The Quiz should contain at least 1 question.')
                return false
            }

            return true
        },

        stickyBox() {
            const stickyElm = document.querySelector('.sticky-box')

            const observer = new IntersectionObserver(
                ([e]) => {
                    e.target.classList.toggle('sticky-box-shadow', e.intersectionRatio < 1)
                },
                { threshold: [1] }
            )

            observer.observe(stickyElm)
        },

        async addRobotQuestions(questionList) {
            for (const question of questionList) {
                this.$store.commit('addNewQuestion', question)
            }

            await this.$nextTick()

            this.saveQuestions()
        }
    }
}
</script>

<style lang="scss">
#question-setting-section {
    .create-question-box {
        column-gap: 0.5rem;
    }

    .sticky-box {
        top: -1px;
        left: 0;
        background-color: #e6eaf2;
        z-index: 1;
        transition: box-shadow 0.3s;

        &.sticky-box-shadow {
            box-shadow: 0px 1px 8px rgba(0, 0, 0, 0.3);
        }
    }

    .question-option {
        column-gap: 0.5rem;
        background-color: #fff;

        .question-sub-option {
            column-gap: 0.5rem;
        }
    }

    .search-box {
        input {
            padding: 0.375rem 2.75rem 0.375rem 0.75rem;
        }

        .search-btn {
            top: 50%;
            right: 0.4rem;
            transform: translateY(-50%);
            padding: 0;
            height: calc(100% - 0.4rem);
            aspect-ratio: 1 / 1;
        }
    }

    .question-list {
        height: 120px;
        background-color: #fff;

        &.new::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            border: 3px solid transparent;
            box-sizing: border-box;
            -webkit-box-sizing: border-box;
            -moz-box-sizing: border-box;
            animation: blink 0.5s;
            animation-iteration-count: 2;

            @keyframes blink {
                50% {
                    border-color: #dc3545;
                }
            }
        }

        .crown-icon {
            top: 1.5rem;
            left: 50%;
            transform: translateX(-50%);
            color: #dc3545;
        }

        .question-image-box {
            aspect-ratio: 1 / 1;
            border-radius: 4px;
            box-shadow: 0 0 12px 2px rgba(0, 0, 0, 0.2);
            .question-image {
                background-repeat: no-repeat;
                background-size: cover;
            }
        }

        .question-title {
            display: -webkit-box;
            overflow: hidden;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            color: #dc3545;
        }

        .draggable-box {
            cursor: -webkit-grab;
            cursor: grab;

            &:active {
                cursor: grabbing;
            }

            &.disabled {
                cursor: default;
            }
        }
    }

    .col-gap-16 {
        column-gap: 1rem;
    }

    .row-gap-16 {
        row-gap: 1rem;
    }

    .col-gap-8 {
        column-gap: 0.5rem;
    }

    .row-gap-8 {
        row-gap: 0.5rem;
    }

    .row-gap-4 {
        row-gap: 0.25rem;
    }

    .ghost {
        opacity: 0.5;
        background: #f4f6f9;
    }
}
</style>
